import React, { useEffect, useState } from 'react';
import { shopApi } from '../../utils/shopApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, KeyRound, Trash2, ShieldCheck } from 'lucide-react';
import { useShopAdminAuth } from '../../context/ShopAdminAuthContext';

const ROLE_LABEL = {
  admin: 'Admin',
  manager: 'Store Manager',
  supervisor: 'Supervisor',
  cashier: 'Cashier',
  rider: 'Rider',
};

const EMPTY = { name: '', email: '', phone: '', password: '', role: 'cashier', permissions: null };

export default function ShopUsersPage() {
  const { staff: me } = useShopAdminAuth();
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], defaults: {} });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pwReset, setPwReset] = useState({ open: false, staffId: null, new_password: '' });

  const load = async () => {
    try {
      const [a, b] = await Promise.all([shopApi.get('/shop/staff'), shopApi.get('/shop/staff/roles')]);
      setList(a.data);
      setMeta(b.data);
    } catch (e) { toast.error('Failed to load staff'); }
  };

  useEffect(() => { load(); }, []);

  const onRoleChange = (newRole) => {
    if (editing) {
      const defaults = meta.defaults[newRole] || {};
      setEditing(s => ({ ...s, role: newRole, permissions: { ...(meta.permissions.reduce((acc, p) => ({ ...acc, [p]: false }), {})), ...defaults } }));
    } else {
      setForm(f => ({ ...f, role: newRole, permissions: null }));
    }
  };

  const togglePerm = (key) => {
    setEditing(s => ({ ...s, permissions: { ...(s.permissions || {}), [key]: !s.permissions?.[key] } }));
  };

  const submitNew = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('Fill all required fields'); return; }
    try {
      await shopApi.post('/shop/staff', { ...form, permissions: form.permissions || undefined });
      toast.success('Staff member created');
      setCreating(false); setForm(EMPTY); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    try {
      await shopApi.put(`/shop/staff/${editing.id}`, {
        name: editing.name, role: editing.role, phone: editing.phone,
        permissions: editing.permissions, active: editing.active,
      });
      toast.success('Staff updated');
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await shopApi.delete(`/shop/staff/${s.id}`);
      toast.success('Deleted');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const submitReset = async () => {
    try {
      await shopApi.post(`/shop/staff/${pwReset.staffId}/reset-password`, { new_password: pwReset.new_password });
      toast.success('Password reset');
      setPwReset({ open: false, staffId: null, new_password: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold flex items-center gap-2"><Users className="w-7 h-7 text-primary" />Shop Staff</h1>
          <p className="text-sm text-muted-foreground">Manage cashiers, supervisors, managers, riders and admins.</p>
        </div>
        <Button onClick={() => { setCreating(true); setForm(EMPTY); }} data-testid="shop-staff-new-btn"><Plus className="w-4 h-4 mr-1" />Add staff</Button>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Status</th><th></th></tr></thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} className="text-center p-10 text-muted-foreground">No staff yet — add your first cashier.</td></tr>
            ) : list.map(s => (
              <tr key={s.id} className="border-t border-border hover:bg-muted/20" data-testid={`shop-staff-row-${s.id}`}>
                <td className="p-3 font-medium">{s.name}{s.id === me?.id && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}</td>
                <td className="p-3 text-muted-foreground">{s.email}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary capitalize">{ROLE_LABEL[s.role] || s.role}</span></td>
                <td className="p-3 text-xs">{s.active ? <span className="text-green-700">Active</span> : <span className="text-muted-foreground">Disabled</span>}</td>
                <td className="p-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setPwReset({ open: true, staffId: s.id, new_password: '' })} data-testid={`shop-staff-reset-${s.id}`}><KeyRound className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...s, permissions: { ...(s.permissions || {}) } })} data-testid={`shop-staff-edit-${s.id}`}>Edit</Button>
                  {s.id !== me?.id && <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`shop-staff-delete-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE dialog */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add staff member</DialogTitle>
            <DialogDescription>Create a new cashier, supervisor, manager, rider or admin for the shop back-office.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} data-testid="new-staff-name" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={onRoleChange}>
                  <SelectTrigger data-testid="new-staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(ROLE_LABEL).map(k => <SelectItem key={k} value={k}>{ROLE_LABEL[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} data-testid="new-staff-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} data-testid="new-staff-phone" /></div>
            <div><Label>Temporary password</Label><Input type="password" value={form.password} onChange={(e) => setForm(f => ({...f, password: e.target.value}))} placeholder="Min 6 characters" data-testid="new-staff-password" /></div>
            <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2 flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" /><span>Default permissions for <strong className="capitalize">{ROLE_LABEL[form.role]}</strong> will be applied. Edit later for fine-grained control.</span></div>
            <Button onClick={submitNew} className="w-full" data-testid="new-staff-submit">Create staff member</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>{editing.name}</DialogTitle>
                <DialogDescription>Update this staff member's role, permissions and active status.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing(s => ({...s, name: e.target.value}))} /></div>
                  <div><Label>Role</Label>
                    <Select value={editing.role} onValueChange={onRoleChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.keys(ROLE_LABEL).map(k => <SelectItem key={k} value={k}>{ROLE_LABEL[k]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div><Label>Phone</Label><Input value={editing.phone || ''} onChange={(e) => setEditing(s => ({...s, phone: e.target.value}))} /></div>
                  <div className="flex items-center gap-2 pb-1"><Switch checked={editing.active !== false} onCheckedChange={(v) => setEditing(s => ({...s, active: v}))} data-testid="edit-staff-active" /><Label>Active</Label></div>
                </div>
                <div>
                  <Label>Permissions</Label>
                  <div className="grid grid-cols-2 gap-1 mt-2 max-h-56 overflow-y-auto border border-border rounded-lg p-2" data-testid="edit-staff-perms">
                    {(meta.permissions || []).map(p => (
                      <label key={p} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/40 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!editing.permissions?.[p]} onChange={() => togglePerm(p)} data-testid={`edit-perm-${p}`} />
                        <span className="font-mono">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button onClick={saveEdit} className="w-full" data-testid="edit-staff-save">Save changes</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Password reset */}
      <Dialog open={pwReset.open} onOpenChange={(o) => !o && setPwReset({ open: false, staffId: null, new_password: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for this staff account. They'll need it on next sign-in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="password" placeholder="New password (min 6 chars)" value={pwReset.new_password} onChange={(e) => setPwReset(p => ({...p, new_password: e.target.value}))} data-testid="reset-pw-input" />
            <Button onClick={submitReset} className="w-full" data-testid="reset-pw-submit">Reset</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
